/**
 * Converts a cron expression to a human-readable description
 * @param cronExpression - The cron expression (e.g., "0 14 * * *")
 * @returns A friendly description (e.g., "Daily at 2:00 PM")
 */
export function cronToFriendly(cronExpression: string): string {
  if (!cronExpression || cronExpression.trim() === '') {
    return 'No schedule';
  }

  const parts = cronExpression.trim().split(/\s+/);
  if (parts.length !== 5) {
    return cronExpression; // Return as-is if not a standard 5-part cron
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Helper to format time
  const formatTime = (h: string, m: string): string => {
    const hourNum = parseInt(h, 10);
    const minuteNum = parseInt(m, 10);
    if (isNaN(hourNum) || isNaN(minuteNum)) {
      return `${h}:${m.padStart(2, '0')}`;
    }
    const hour12 = hourNum % 12 || 12;
    const ampm = hourNum >= 12 ? 'PM' : 'AM';
    const minuteStr = minuteNum.toString().padStart(2, '0');
    return `${hour12}:${minuteStr} ${ampm}`;
  };

  // Helper to get day name
  const getDayName = (day: string): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayNum = parseInt(day, 10);
    if (!isNaN(dayNum) && dayNum >= 0 && dayNum <= 6) {
      return days[dayNum];
    }
    return '';
  };

  const timeStr = formatTime(hour, minute);

  // Every minute
  if (minute !== '*' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    if (minute.startsWith('*/')) {
      const interval = minute.substring(2);
      return `Every ${interval} minute${interval !== '1' ? 's' : ''}`;
    }
    return `Every minute at :${minute.padStart(2, '0')}`;
  }

  // Every hour
  if (minute !== '*' && hour.startsWith('*/') && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    const interval = hour.substring(2);
    return `Every ${interval} hour${interval !== '1' ? 's' : ''} at :${minute.padStart(2, '0')}`;
  }

  // Daily (every day at specific time)
  if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    // Check for "Occurs Every" pattern with hours: "0 5-20/1 * * *" (every 1 hour from 5 AM to 8 PM)
    if (hour.includes('-') && hour.includes('/')) {
      const [range, interval] = hour.split('/');
      const [startHour, endHour] = range.split('-');
      const startHourNum = parseInt(startHour, 10);
      const endHourNum = parseInt(endHour, 10);
      const intervalNum = parseInt(interval, 10);
      
      if (!isNaN(startHourNum) && !isNaN(endHourNum) && !isNaN(intervalNum)) {
        const startTime = formatTime(startHour, minute);
        const endTime = formatTime(endHour, minute);
        const intervalStr = intervalNum === 1 ? 'hour' : `${intervalNum} hours`;
        return `Daily, every ${intervalStr} from ${startTime} to ${endTime}`;
      }
    }
    
    // Check for "Occurs Every" pattern with minutes: "*/30 8-18 * * *" (every 30 minutes from 8 AM to 6 PM)
    if (minute.startsWith('*/') && hour.includes('-') && !hour.includes('/')) {
      const minuteInterval = parseInt(minute.substring(2), 10);
      const [startHour, endHour] = hour.split('-');
      const startHourNum = parseInt(startHour, 10);
      const endHourNum = parseInt(endHour, 10);
      
      if (!isNaN(minuteInterval) && !isNaN(startHourNum) && !isNaN(endHourNum)) {
        const startTime = formatTime(startHour, '0');
        const endTime = formatTime(endHour, '59');
        const intervalStr = minuteInterval === 1 ? 'minute' : `${minuteInterval} minutes`;
        return `Daily, every ${intervalStr} from ${startTime} to ${endTime}`;
      }
    }
    
    // Check for every N hours pattern
    if (hour.startsWith('*/')) {
      const interval = hour.substring(2);
      return `Every ${interval} hour${interval !== '1' ? 's' : ''} at :${minute.padStart(2, '0')}`;
    }
    
    return `Daily at ${timeStr}`;
  }

  // Weekly (specific day of week)
  if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
    if (dayOfWeek.includes(',')) {
      const days = dayOfWeek.split(',').map(d => getDayName(d.trim())).filter(Boolean);
      if (days.length > 0) {
        return `Weekly on ${days.join(', ')} at ${timeStr}`;
      }
    } else if (dayOfWeek.includes('-')) {
      const [start, end] = dayOfWeek.split('-').map(d => getDayName(d.trim()));
      if (start && end) {
        return `Weekly from ${start} to ${end} at ${timeStr}`;
      }
    } else {
      const dayName = getDayName(dayOfWeek);
      if (dayName) {
        return `Weekly on ${dayName} at ${timeStr}`;
      }
    }
  }

  // Monthly (specific day of month)
  if (dayOfMonth !== '*' && month === '*' && dayOfWeek === '*') {
    if (dayOfMonth.includes(',')) {
      const days = dayOfMonth.split(',').join(', ');
      return `Monthly on days ${days} at ${timeStr}`;
    } else if (dayOfMonth.includes('-')) {
      return `Monthly from day ${dayOfMonth} at ${timeStr}`;
    } else {
      const day = parseInt(dayOfMonth, 10);
      const suffix = getOrdinalSuffix(day);
      return `Monthly on the ${day}${suffix} at ${timeStr}`;
    }
  }

  // Yearly (specific month and day)
  if (dayOfMonth !== '*' && month !== '*' && dayOfWeek === '*') {
    const months = ['', 'January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    const monthNum = parseInt(month, 10);
    const monthName = months[monthNum] || month;
    const day = parseInt(dayOfMonth, 10);
    const suffix = getOrdinalSuffix(day);
    return `Yearly on ${monthName} ${day}${suffix} at ${timeStr}`;
  }

  // Nth weekday of month (e.g., "0 2 * * 1#2" = 2nd Monday)
  if (dayOfWeek.includes('#')) {
    const [weekday, nth] = dayOfWeek.split('#');
    const dayName = getDayName(weekday);
    if (dayName) {
      const nthNum = parseInt(nth, 10);
      if (nthNum === -1 || nth === 'L') {
        return `Monthly on the last ${dayName} at ${timeStr}`;
      } else {
        const nthStr = getNthString(nthNum);
        return `Monthly on the ${nthStr} ${dayName} at ${timeStr}`;
      }
    }
  }

  // Default: return the cron expression if we can't parse it
  return cronExpression;
}

function getOrdinalSuffix(n: number): string {
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}

function getNthString(n: number): string {
  if (n === 1) return 'first';
  if (n === 2) return 'second';
  if (n === 3) return 'third';
  if (n === 4) return 'fourth';
  if (n === 5) return 'fifth';
  return `${n}th`;
}
